import { memo, useState } from "react";
import { decodeEntities } from "@/lib/sanitize";
import { fixAcronyms } from "@/lib/acronyms";
import ShareEpisode from "@/components/ShareEpisode";
import { Link } from "react-router-dom";
import { usePrefetchOnIntent } from "@/hooks/usePrefetchOnIntent";
import { usePrefetchOnVisible } from "@/hooks/usePrefetchOnVisible";
import OfflineEpisodeButton from "@/components/OfflineEpisodeButton";
import OfflineReadyBadge from "@/components/OfflineReadyBadge";

import { MockEpisode } from "@/data/mockData";
import {
  Bookmark,
  BookmarkCheck,
  ThumbsUp,
  ThumbsDown,
  FileText,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ListPlus,
} from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";
import AddToPlaylist from "@/components/AddToPlaylist";
import NoteButton from "@/components/NoteButton";
import { PlatformBadges } from "@/components/PlatformBadges";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface ResultCardProps {
  episode: MockEpisode;
  rank: number;
  onSave?: () => void;
  onFindSimilar?: () => void;
  isSaved?: boolean;
  feedback?: "like" | "dislike" | null;
  onFeedback?: (type: "like" | "dislike") => void;
  searchQuery?: string;
}

// Branded SVG badges
function SpotifyBadge() {
  return (
    <svg viewBox="0 0 168 40" className="h-8 sm:h-9 max-w-full" preserveAspectRatio="xMinYMid meet" aria-label="Listen on Spotify">
      <rect width="168" height="40" rx="6" fill="#1DB954" />
      <g fill="white">
        <circle cx="20" cy="20" r="10" fill="none" />
        <path d="M20 10a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.6 14.4a.6.6 0 0 1-.84.2c-2.3-1.4-5.2-1.72-8.6-.94a.6.6 0 1 1-.28-1.18c3.72-.84 6.92-.48 9.52 1.08a.6.6 0 0 1 .2.84zm1.24-2.72a.78.78 0 0 1-1.06.24c-2.64-1.62-6.66-2.1-9.78-1.14a.78.78 0 0 1-.44-1.5c3.56-1.08 7.98-.56 11.04 1.32a.78.78 0 0 1 .24 1.08zm.1-2.84c-3.16-1.88-8.38-2.04-11.4-1.14a.94.94 0 1 1-.54-1.8c3.46-1.04 9.22-.84 12.86 1.32a.94.94 0 0 1-1.92 1.62z" />
      </g>
      <text x="36" y="16" fill="white" fontFamily="system-ui, sans-serif" fontSize="9" fontWeight="400">Listen on</text>
      <text x="36" y="29" fill="white" fontFamily="system-ui, sans-serif" fontSize="13" fontWeight="700">Spotify</text>
    </svg>
  );
}

function ApplePodcastsBadge() {
  return (
    <svg viewBox="0 0 200 40" className="h-8 sm:h-9 max-w-full" preserveAspectRatio="xMinYMid meet" aria-label="Listen on Apple Podcasts">
      <rect width="200" height="40" rx="6" fill="#000000" />
      <g transform="translate(8,6) scale(0.55)">
        <linearGradient id="ap-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F452FF" />
          <stop offset="1" stopColor="#832BC1" />
        </linearGradient>
        <rect width="50" height="50" rx="11" fill="url(#ap-grad)" />
        <g fill="white">
          <circle cx="25" cy="22" r="4" />
          <path d="M25 8a14 14 0 0 0-9 24.7l1.6-5a9.4 9.4 0 1 1 14.8 0l1.6 5A14 14 0 0 0 25 8z" />
          <path d="M25 14a8 8 0 0 0-5.4 13.9l1.2-3.6a4.8 4.8 0 1 1 8.4 0l1.2 3.6A8 8 0 0 0 25 14z" />
          <path d="M22.2 29.5c-.3 1.2-.5 2.5-.4 3.5.1 1.4.6 2 1.6 2.3.6.2 1 .2 1.6.2s1 0 1.6-.2c1-.3 1.5-.9 1.6-2.3.1-1-.1-2.3-.4-3.5l-.8-3a4 4 0 0 0-4.8 0l-.8 3z" />
        </g>
      </g>
      <text x="40" y="16" fill="white" fontFamily="system-ui, sans-serif" fontSize="9" fontWeight="400">Listen on</text>
      <text x="40" y="29" fill="white" fontFamily="system-ui, sans-serif" fontSize="12" fontWeight="700">Apple Podcasts</text>
    </svg>
  );
}

function ResultCardImpl({ episode, rank, onSave, onFindSimilar, isSaved = false, feedback: externalFeedback, onFeedback, searchQuery }: ResultCardProps) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(rank === 1);
  const [internalFeedback, setInternalFeedback] = useState<"up" | "down" | null>(null);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [seekSignal, setSeekSignal] = useState(0);
  const [whyExpanded, setWhyExpanded] = useState(false);
  
  const currentFeedback = externalFeedback !== undefined ? externalFeedback : (internalFeedback === "up" ? "like" : internalFeedback === "down" ? "dislike" : null);

  const confidenceClass =
    episode.confidenceScore >= 90
      ? "confidence-high"
      : episode.confidenceScore >= 75
      ? "confidence-medium"
      : "confidence-low";

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    return `${m} min`;
  };

  const formatTimestamp = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const spotifyLink = episode.spotifyEpisodeUrl || episode.spotifyUrl;
  const appleLink = episode.appleEpisodeUrl || episode.applePodcastsUrl;

  const SaveIcon = isSaved ? BookmarkCheck : Bookmark;

  // Mobile has no hover — when the card scrolls into view, warm the episode
  // route chunk so a tap loads instantly. Desktop hover prefetch on the title
  // link still fires for pointer users; both guards short-circuit after one hit.
  const visibleRef = usePrefetchOnVisible<HTMLDivElement>(
    () => import("@/pages/EpisodePage"),
  );

  return (
    <div
      ref={visibleRef}
      className={`${rank === 1 ? "card-press" : "card-press-sm"} animate-slide-in-up`}
      style={{ animationDelay: `${rank * 120}ms` }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-5">
        {/* Podcast Artwork */}
        <div className="flex-shrink-0">
          {episode.podcastArtwork ? (
            <img
              src={episode.podcastArtwork}
              alt={`${episode.podcastName} artwork`}
              // Rank 1 is almost always the mobile LCP candidate; prioritize it
              // and lazy-load the rest so the network doesn't fight the hero.
              loading={rank === 1 ? "eager" : "lazy"}
              {...(rank === 1 ? { fetchPriority: "high" as const } : { fetchPriority: "low" as const })}
              decoding="async"
              className="w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] retro-border object-cover aspect-square"
            />
          ) : (
            <div className="w-[72px] h-[72px] sm:w-[88px] sm:h-[88px] retro-border bg-muted flex items-center justify-center aspect-square">
              <span className="font-retro text-lg sm:text-xl text-muted-foreground">{episode.podcastName.charAt(0)}</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Title — top of card, aligned with top of artwork */}
          <h3 className="font-retro font-bold text-sm sm:text-lg leading-tight mb-1.5">
            {episode.slug ? (
              <Link
                to={`/episode/${episode.slug}`}
                className="hover:text-primary transition-colors hover:underline"
                {...usePrefetchOnIntent(() => import("@/pages/EpisodePage"))}
              >
                {decodeEntities(episode.episodeTitle)}
              </Link>
            ) : (

              decodeEntities(episode.episodeTitle)
            )}
          </h3>

          <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-sm text-muted-foreground font-mono flex-wrap">
            <span>{episode.podcastName}</span>
            {episode.guestName && (
              <>
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline">w/ {episode.guestName}</span>
              </>
            )}
            <span>·</span>
            <span>{new Date(episode.publishDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
            <span>·</span>
            <span>{formatDuration(episode.durationSeconds)}</span>
          </div>
        </div>
      </div>

      {/* Embedded Audio Player — moved up so listening is the first action */}
      {episode.audioUrl && (
        <AudioPlayer
          src={episode.audioUrl}
          title={episode.episodeTitle}
          podcastName={episode.podcastName}
          artworkUrl={episode.podcastArtwork}
          episodeId={episode.id}
          startTime={episode.bestStartSeconds ?? undefined}
          durationSeconds={episode.durationSeconds}
          seekSignal={seekSignal}
        />
      )}

      {/* Match score + jump badge — full-width row below the player */}
      <div className="px-3 sm:px-5 pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`confidence-badge ${confidenceClass} inline-flex items-center gap-1 text-sm sm:text-base font-bold px-2 py-0.5 cursor-pointer hover:opacity-90 transition-opacity`}
                aria-label={`${episode.confidenceScore}% match — tap to learn more`}
              >
                <span className="tabular-nums">{episode.confidenceScore}%</span>
                <span className="text-[10px] sm:text-xs uppercase tracking-wider opacity-90">Match</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 text-xs sm:text-sm space-y-2" align="start">
              <p className="font-retro font-bold text-sm">What does {episode.confidenceScore}% mean?</p>
              <p className="leading-relaxed">
                It's how confident we are this episode actually helps with your question. The higher the score, the more value it adds.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                We blend three signals: how closely the conversation matches your meaning, how often your exact words show up, and a final AI pass that reads the relevant moments and judges real usefulness.
              </p>
              <ul className="text-[11px] font-mono text-muted-foreground space-y-0.5 pt-1 border-t border-border">
                <li><span className="text-confidence-high font-bold">90%+</span> — spot on, listen first</li>
                <li><span className="font-bold">75–89%</span> — strong match, worth your time</li>
                <li><span className="font-bold">below 75%</span> — adjacent, may still spark ideas</li>
              </ul>
            </PopoverContent>
          </Popover>
          <OfflineReadyBadge episodeId={episode.id} audioUrl={episode.audioUrl} />
          {episode.audioUrl && episode.id && (
            <OfflineEpisodeButton
              episodeId={episode.id}
              audioUrl={episode.audioUrl}
              title={episode.episodeTitle}
              podcastName={episode.podcastName}
              artworkUrl={episode.podcastArtwork}
              durationSeconds={episode.durationSeconds}
            />
          )}
          {episode.bestStartSeconds != null && episode.bestStartSeconds > 30 && episode.audioUrl && (
            <button
              type="button"
              onClick={() => setSeekSignal((n) => n + 1)}
              title="Click to jump to the most relevant moment in the audio player"
              className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 py-0.5 border-2 border-accent text-accent bg-accent/10 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
              aria-label={`Jump to ${formatTimestamp(episode.bestStartSeconds)} — the most relevant moment`}
            >
              <span aria-hidden>⏱</span>
              <span>Jumps to {formatTimestamp(episode.bestStartSeconds)}</span>
            </button>
          )}
        </div>
      </div>

      {/* Action button strip — single horizontally-scrollable row, never wraps.
          Lives full-width below the header (where the red line was in the screenshot). */}
      <div className="px-3 sm:px-5 pb-3">
        <div
          className="flex items-center gap-2 overflow-x-auto flex-nowrap scrollbar-thin -mx-3 sm:-mx-5 px-3 sm:px-5 py-1"
          style={{ scrollbarWidth: "thin" }}
        >
          <button
            onClick={onSave}
            className="flex-shrink-0 whitespace-nowrap comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)] shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95"
          >
            <SaveIcon className="w-3 h-3 relative z-10" /> <span className="relative z-10">{isSaved ? "Saved" : "Save"}</span>
          </button>
          {/* Offline download moved up to the match-score row to keep the
              action strip from overflowing on narrow screens. */}
          <button
            onClick={onFindSimilar}
            className="flex-shrink-0 whitespace-nowrap comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)] shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95"
          >
            <Sparkles className="w-3 h-3 relative z-10" />
            <span className="hidden sm:inline relative z-10">More like this</span>
            <span className="sm:hidden relative z-10">Similar</span>
          </button>
          {user && episode.id && (
            <button
              onClick={() => setPlaylistOpen(true)}
              className="flex-shrink-0 whitespace-nowrap comic-play-btn relative overflow-hidden flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-2 sm:px-3 py-1 border-[2px] rounded-md bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] border-[hsl(195,80%,28%)] shadow-[3px_3px_0px_0px_hsl(var(--foreground))] hover:shadow-[1px_1px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 transform hover:scale-[1.03] hover:rotate-[-0.5deg] active:scale-95"
            >
              <ListPlus className="w-3 h-3 relative z-10" />
              <span className="hidden sm:inline relative z-10">Add to list</span>
              <span className="sm:hidden relative z-10">List</span>
            </button>
          )}
          {user && episode.id && (
            <div className="flex-shrink-0 whitespace-nowrap">
              <NoteButton
                episodeId={episode.id}
                episodeTitle={episode.episodeTitle}
                audioUrl={episode.audioUrl}
                durationSeconds={episode.durationSeconds}
                podcastName={episode.podcastName}
                artworkUrl={episode.podcastArtwork}
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="px-3 sm:px-5 pb-3">
        <p className="text-xs sm:text-sm leading-relaxed">{episode.summary}</p>
      </div>

      {/* Expandable details */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 sm:px-5 py-2 flex items-center gap-2 text-xs font-mono text-accent hover:text-retro-teal transition-colors border-t border-border"
      >
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {expanded ? "Less detail" : "Why this episode?"}
      </button>

      {expanded && (
        <div className="px-3 sm:px-5 pb-4 space-y-3 border-t border-border pt-3 animate-fade-in">
          <div>
            <button
              type="button"
              onClick={() => setWhyExpanded(!whyExpanded)}
              className="w-full flex items-center justify-between text-[10px] sm:text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1 hover:text-accent transition-colors"
              aria-expanded={whyExpanded}
              aria-label="Toggle why this episode was chosen"
            >
              <span>Why it matched</span>
              {whyExpanded ? <ChevronUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
            </button>
            {whyExpanded && (
              <p className="text-xs sm:text-sm leading-relaxed">{episode.whyChosen}</p>
            )}
          </div>
          <div>
            <p className="text-[10px] sm:text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
              Relevant discussion
            </p>
            <p className="text-xs sm:text-sm leading-relaxed italic">"{episode.relevantDiscussion}"</p>
          </div>
          <div>
            <p className="text-[10px] sm:text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
              Topics covered
            </p>
            <div className="flex flex-wrap gap-1">
              {episode.topics.map((t) => (
                <span
                  key={t}
                  className="text-[10px] sm:text-xs font-mono px-1.5 sm:px-2 py-0.5 bg-muted text-muted-foreground"
                >
                  {fixAcronyms(t)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}




      {/* Platform Buttons + Feedback — centered */}
      <div className="px-3 sm:px-5 py-3 border-t border-border space-y-3">
        {(spotifyLink || appleLink) && (
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <PlatformBadges spotifyUrl={spotifyLink} appleUrl={appleLink} />
            </div>
            {episode.bestStartSeconds != null && episode.bestStartSeconds > 30 && (
              episode.audioUrl ? (
                <button
                  onClick={() => setSeekSignal((n) => n + 1)}
                  className="font-mono text-[10px] sm:text-xs text-muted-foreground italic hover:text-accent transition-colors cursor-pointer"
                >
                  ⏱ Skip to <span className="font-bold text-accent underline">{formatTimestamp(episode.bestStartSeconds)}</span> for the relevant part
                </button>
              ) : (
                <p className="font-mono text-[10px] sm:text-xs text-muted-foreground italic">
                  ⏱ Skip to <span className="font-bold text-accent">{formatTimestamp(episode.bestStartSeconds)}</span> for the relevant part
                </p>
              )
            )}
          </div>
        )}

        {/* Share + Thumbs feedback row */}
        <div className={`flex items-center ${episode.slug ? "justify-between" : "justify-end"}`}>
          {episode.slug && (
            <ShareEpisode
              episodeSlug={episode.slug}
              episodeTitle={episode.episodeTitle}
              podcastName={episode.podcastName}
              variant="minimal"
            />
          )}
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => onFeedback ? onFeedback("like") : setInternalFeedback(internalFeedback === "up" ? null : "up")}
              className={`p-1 sm:p-1.5 transition-colors ${
                currentFeedback === "like" ? "text-confidence-high" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ThumbsUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <button
              onClick={() => onFeedback ? onFeedback("dislike") : setInternalFeedback(internalFeedback === "down" ? null : "down")}
              className={`p-1 sm:p-1.5 transition-colors ${
                currentFeedback === "dislike" ? "text-destructive" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ThumbsDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>

      {playlistOpen && episode.id && (
        <AddToPlaylist open={playlistOpen} onClose={() => setPlaylistOpen(false)} episodeId={episode.id} searchQuery={searchQuery} />
      )}
    </div>
  );
}

// Memoized so toggling save/feedback on one card doesn't re-render every
// sibling. SearchResults paints up to 5 cards — without memo, every Set/Map
// rebuild on save or feedback re-rendered all of them and ran the audio
// player's mount logic too.
const ResultCard = memo(ResultCardImpl);
export default ResultCard;
